import React, { useState, useEffect } from "react";
import { useAuth, useThemeMode, retroColors } from '@vortx/shared-ui';
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Paper, TextField, Button, Typography, Box, Alert, CircularProgress
} from "@mui/material";
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { API_BASE_URL } from '../services/api';

export default function JoinPage() {
    const [searchParams] = useSearchParams();
    const inviteId = searchParams.get('invite');

    const [invite, setInvite] = useState(null);
    const [loadingInvite, setLoadingInvite] = useState(true);
    const [inviteError, setInviteError] = useState(null);

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const { signup, currentUser } = useAuth();
    const navigate = useNavigate();
    const { isRetro } = useThemeMode();

    // Fetch invite details
    useEffect(() => {
        async function fetchInvite() {
            if (!inviteId) {
                setInviteError("No invite specified. Please use the link from your invitation email.");
                setLoadingInvite(false);
                return;
            }

            try {
                const response = await fetch(`${API_BASE_URL}/api/v1/admin/invites/${inviteId}`);
                if (!response.ok) {
                    if (response.status === 404) {
                        setInviteError("This invite was not found or has already been used.");
                    } else {
                        setInviteError("Failed to load invite details.");
                    }
                    setLoadingInvite(false);
                    return;
                }

                const data = await response.json();
                setInvite(data.invite);
                setEmail(data.invite.email);
            } catch (err) {
                setInviteError("Failed to connect to server.");
            }
            setLoadingInvite(false);
        }

        fetchInvite();
    }, [inviteId]);

    // Redirect authenticated users to dashboard
    useEffect(() => {
        if (currentUser) {
            navigate('/dashboard', { replace: true });
        }
    }, [currentUser, navigate]);

    async function handleSubmit(e) {
        e.preventDefault();
        setError("");

        if (password !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        if (password.length < 6) {
            setError("Password must be at least 6 characters");
            return;
        }

        setLoading(true);

        try {
            await signup(email, password);
            navigate("/dashboard");
        } catch (err) {
            setError(err.message || "Failed to create account");
        }

        setLoading(false);
    }

    // Loading state
    if (loadingInvite) {
        return (
            <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isRetro ? retroColors.bg : 'background.default'
            }}>
                <CircularProgress sx={{ color: vortxColors.gold }} />
            </Box>
        );
    }

    // Error state
    if (inviteError) {
        return (
            <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isRetro ? retroColors.bg : 'background.default',
                p: 3
            }}>
                <Paper sx={{
                    p: 4,
                    maxWidth: 500,
                    bgcolor: isRetro ? '#111' : 'background.paper',
                    border: `1px solid ${vortxColors.gold}`,
                    textAlign: 'center'
                }}>
                    <Typography
                        sx={{
                            fontFamily: vortxTypography.mono,
                            color: '#f44336',
                            fontSize: '0.75rem',
                            letterSpacing: 2,
                            mb: 2
                        }}
                    >
                        {/* ERROR */}
                    </Typography>
                    <Typography sx={{ color: isRetro ? '#fff' : 'text.primary', mb: 3 }}>
                        {inviteError}
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/login')}
                        sx={{
                            color: vortxColors.gold,
                            borderColor: vortxColors.gold,
                            fontFamily: vortxTypography.mono,
                            '&:hover': {
                                borderColor: vortxColors.gold,
                                bgcolor: 'rgba(178, 140, 80, 0.1)'
                            }
                        }}
                    >
                        Go to Login
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isRetro ? retroColors.bg : 'background.default',
            p: 3
        }}>
            <Paper sx={{
                p: 0,
                maxWidth: 480,
                width: '100%',
                bgcolor: isRetro ? retroColors.bg : 'background.paper',
                border: `1px solid ${vortxColors.gold}`,
                overflow: 'hidden'
            }}>
                {/* Header */}
                <Box sx={{
                    background: isRetro ? 'linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 100%)' : 'none',
                    p: 3,
                    borderBottom: `1px solid ${vortxColors.gold}`,
                    textAlign: 'center'
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        color: isRetro ? retroColors.muted : 'text.secondary',
                        letterSpacing: 3,
                        textTransform: 'uppercase',
                        mb: 1
                    }}>
                        {/* incoming transmission */}
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '1.5rem',
                        color: vortxColors.gold,
                        letterSpacing: 4,
                        fontWeight: 400
                    }}>
                        VORTX FLUX
                    </Typography>
                </Box>

                {/* Content */}
                <Box sx={{ p: 4 }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        color: vortxColors.gold,
                        fontSize: '0.75rem',
                        letterSpacing: 1,
                        mb: 2
                    }}>
                        &gt; INITIALIZE_ACCOUNT
                    </Typography>

                    <Typography sx={{
                        color: isRetro ? '#fff' : 'text.primary',
                        fontSize: '1.1rem',
                        mb: 1
                    }}>
                        You've been granted access to the{' '}
                        <Box component="span" sx={{ color: vortxColors.gold, fontWeight: 600 }}>
                            {invite?.company_name || 'team'}
                        </Box>
                        {' '}team account for Vortx Flux
                    </Typography>

                    {invite?.invited_by_email && (
                        <Typography sx={{
                            color: isRetro ? retroColors.muted : 'text.secondary',
                            fontSize: '0.85rem',
                            mb: 3
                        }}>
                            Invited by {invite.invited_by_email}
                        </Typography>
                    )}

                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 2,
                                bgcolor: 'rgba(244, 67, 54, 0.1)',
                                color: '#f44336',
                                border: '1px solid rgba(244, 67, 54, 0.3)',
                                '& .MuiAlert-icon': { color: '#f44336' }
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* Email field - pre-filled and readonly */}
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.7rem',
                                color: vortxColors.gold,
                                letterSpacing: 1,
                                mb: 0.5
                            }}>
                                AUTH_EMAIL
                            </Typography>
                            <TextField
                                value={email}
                                fullWidth
                                disabled
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: isRetro ? retroColors.surface : 'background.default',
                                        fontFamily: vortxTypography.mono,
                                        '& fieldset': { borderColor: 'rgba(63,106,100,0.5)' },
                                    },
                                    '& .MuiInputBase-input.Mui-disabled': {
                                        color: isRetro ? '#888' : 'text.disabled',
                                        WebkitTextFillColor: isRetro ? '#888' : undefined
                                    }
                                }}
                            />
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.65rem',
                                color: isRetro ? retroColors.muted : 'text.secondary',
                                mt: 0.5
                            }}>
                                This email is linked to your invite
                            </Typography>
                        </Box>

                        {/* Password field */}
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.7rem',
                                color: vortxColors.gold,
                                letterSpacing: 1,
                                mb: 0.5
                            }}>
                                SET_PASSWORD
                            </Typography>
                            <TextField
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                fullWidth
                                size="small"
                                placeholder="Enter password"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: isRetro ? retroColors.surface : 'background.default',
                                        fontFamily: vortxTypography.mono,
                                        color: isRetro ? '#fff' : 'text.primary',
                                        '& fieldset': { borderColor: 'rgba(63,106,100,0.5)' },
                                        '&:hover fieldset': { borderColor: vortxColors.gold },
                                        '&.Mui-focused fieldset': { borderColor: vortxColors.gold },
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: isRetro ? retroColors.muted : 'text.disabled',
                                        opacity: 1
                                    }
                                }}
                            />
                        </Box>

                        {/* Confirm Password field */}
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.7rem',
                                color: vortxColors.gold,
                                letterSpacing: 1,
                                mb: 0.5
                            }}>
                                CONFIRM_PASSWORD
                            </Typography>
                            <TextField
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                fullWidth
                                size="small"
                                placeholder="Confirm password"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: isRetro ? retroColors.surface : 'background.default',
                                        fontFamily: vortxTypography.mono,
                                        color: isRetro ? '#fff' : 'text.primary',
                                        '& fieldset': { borderColor: 'rgba(63,106,100,0.5)' },
                                        '&:hover fieldset': { borderColor: vortxColors.gold },
                                        '&.Mui-focused fieldset': { borderColor: vortxColors.gold },
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: isRetro ? retroColors.muted : 'text.disabled',
                                        opacity: 1
                                    }
                                }}
                            />
                        </Box>

                        {/* Submit button */}
                        <Button
                            type="submit"
                            fullWidth
                            disabled={loading}
                            sx={{
                                bgcolor: vortxColors.gold,
                                color: isRetro ? '#0a0a0a' : vortxColors.green,
                                fontFamily: vortxTypography.mono,
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                letterSpacing: 2,
                                py: 1.5,
                                '&:hover': {
                                    bgcolor: '#c9a050'
                                },
                                '&.Mui-disabled': {
                                    bgcolor: isRetro ? 'rgba(255,255,255,0.25)' : 'action.disabledBackground',
                                    color: isRetro ? retroColors.muted : 'text.disabled'
                                }
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={20} sx={{ color: isRetro ? '#0a0a0a' : vortxColors.green }} />
                            ) : (
                                'CREATE ACCOUNT'
                            )}
                        </Button>
                    </form>

                    {/* Already have account link */}
                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Typography
                            component="span"
                            onClick={() => navigate('/login')}
                            sx={{
                                color: isRetro ? retroColors.muted : 'text.secondary',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                '&:hover': { color: vortxColors.gold }
                            }}
                        >
                            Already have an account? Log in
                        </Typography>
                    </Box>
                </Box>

                {/* Footer */}
                <Box sx={{
                    p: 2,
                    bgcolor: isRetro ? '#050505' : 'background.default',
                    borderTop: isRetro ? '1px solid #222' : '1px solid rgba(0,0,0,0.12)',
                    textAlign: 'center'
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        color: isRetro ? retroColors.muted : 'text.secondary',
                        letterSpacing: 1
                    }}>
                        VORTX ENERGY
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
}
